/**
 * Collapses the long parts of a product description into an accordion.
 *
 * The descriptions are pasted from PDFs, so their markup is inconsistent —
 * markers like "INCI:", "MODO D'USO" and "PRINCIPI ATTIVI" appear as plain text
 * inside <p>/<span>, not as headings. Rather than parse that HTML, this walks
 * the description the browser has already rendered and regroups the top-level
 * blocks by marker.
 *
 * Safe by construction: a description with no recognised marker is left exactly
 * as it is, and if this script never runs the full text is still readable.
 */
(function () {
  'use strict';

  // order matters: longest / most specific first
  var SECTIONS = [
    { key: 'inci',    label: 'Ingredienti (INCI)', open: false, test: /^\s*INCI\b/i },
    { key: 'uso',     label: "Modo d'uso",         open: true,  test: /^\s*MODO\s*D\s*[''’`]?\s*USO/i },
    { key: 'attivi',  label: 'Principi attivi',    open: true,  test: /^\s*PRINCIPI\s+ATTIVI/i }
  ];

  function textOf(el) {
    return (el.textContent || '').replace(/ /g, ' ').trim();
  }

  function matchMarker(el) {
    var t = textOf(el);
    if (!t) return null;
    for (var i = 0; i < SECTIONS.length; i++) {
      if (SECTIONS[i].test.test(t)) return SECTIONS[i];
    }
    return null;
  }

  /**
   * Lift the contents of every plain wrapper DIV up to the container.
   *
   * The named wrappers are what the PDF paste produces. div.section is what
   * Shopify's own rich-text editor wraps a description in, and it is the
   * reason SIERO CIGLIA had its whole description as a single top-level node
   * with the INCI paragraph buried one level down where the marker scan never
   * looked. Rather than keep a list of class names that has already been wrong
   * once, unwrap ANY div that only holds block-level children -- a div used
   * for layout, never one being used as a paragraph.
   */
  function flatten(container) {
    for (var pass = 0; pass < 5; pass++) {
      var divs = container.querySelectorAll('div');
      var unwrapped = 0;

      Array.prototype.forEach.call(divs, function (d) {
        if (!d.parentNode) return;
        if (!d.children.length) return;           // a div holding only text is a paragraph
        // any non-whitespace text directly inside means it is carrying content itself
        for (var i = 0; i < d.childNodes.length; i++) {
          var n = d.childNodes[i];
          if (n.nodeType === 3 && n.nodeValue.replace(/\u00a0/g, ' ').trim()) return;
        }
        while (d.firstChild) d.parentNode.insertBefore(d.firstChild, d);
        d.parentNode.removeChild(d);
        unwrapped++;
      });

      if (!unwrapped) return;
    }
  }

  /**
   * Split a block at its <br> boundaries into one node per visual line.
   *
   * Concetta's descriptions frequently put a closing sentence and the
   * "PRINCIPI ATTIVI:" heading inside the same <p>, separated by <br>. The
   * marker test is anchored at the start of a block, so before this those
   * sections were silently left out of the accordion -- 7 of 43 products.
   */
  function splitOnBreaks(el) {
    if (!el.querySelector || !el.querySelector('br')) return [el];

    var pieces = [];
    var current = el.cloneNode(false);
    current.removeAttribute('id');

    function flush() {
      if (current.textContent.replace(/\u00a0/g, ' ').trim() || current.querySelector('img')) {
        pieces.push(current);
      }
      current = el.cloneNode(false);
      current.removeAttribute('id');
    }

    Array.prototype.slice.call(el.childNodes).forEach(function (node) {
      if (node.nodeType === 1 && node.tagName === 'BR') { flush(); return; }
      current.appendChild(node.cloneNode(true));
    });
    flush();

    if (pieces.length < 2) return [el];

    pieces.forEach(function (piece) { el.parentNode.insertBefore(piece, el); });
    el.parentNode.removeChild(el);
    return pieces;
  }

  function build(container) {
    flatten(container);

    // one node per visual line, so a marker that starts a line is found even
    // when it shares a <p> with the paragraph above it
    Array.prototype.slice.call(container.children).forEach(splitOnBreaks);

    var blocks = Array.prototype.slice.call(container.children);
    var groups = [];
    var current = null;

    blocks.forEach(function (el) {
      var marker = matchMarker(el);
      if (marker) {
        current = { spec: marker, nodes: [el] };
        groups.push(current);
      } else if (current) {
        current.nodes.push(el);
      }
    });

    if (!groups.length) return false; // nothing recognised — leave it alone

    var acc = document.createElement('div');
    acc.className = 'pd-accordion';

    groups.forEach(function (group, index) {
      var id = 'pd-panel-' + group.spec.key + '-' + index;

      var item = document.createElement('div');
      item.className = 'pd-accordion__item';

      var trigger = document.createElement('button');
      trigger.type = 'button';
      trigger.className = 'pd-accordion__trigger';
      trigger.setAttribute('aria-expanded', group.spec.open ? 'true' : 'false');
      trigger.setAttribute('aria-controls', id);
      trigger.appendChild(document.createTextNode(group.spec.label));

      var icon = document.createElement('span');
      icon.className = 'pd-accordion__icon';
      icon.setAttribute('aria-hidden', 'true');
      trigger.appendChild(icon);

      var panel = document.createElement('div');
      panel.className = 'pd-accordion__panel pd-accordion__panel--' + group.spec.key + ' rte';
      panel.id = id;
      panel.hidden = !group.spec.open;

      // first node is the marker line itself — the label replaces it
      group.nodes.forEach(function (node, i) {
        if (i === 0) {
          var rest = textOf(node).replace(group.spec.test, '').replace(/^[:\s]+/, '');
          if (rest) {
            var p = document.createElement('p');
            p.textContent = rest;
            panel.appendChild(p);
          }
          node.parentNode.removeChild(node);
          return;
        }
        panel.appendChild(node);
      });

      trigger.addEventListener('click', function () {
        var open = trigger.getAttribute('aria-expanded') === 'true';
        trigger.setAttribute('aria-expanded', open ? 'false' : 'true');
        panel.hidden = open;
      });

      item.appendChild(trigger);
      item.appendChild(panel);
      acc.appendChild(item);
    });

    container.appendChild(acc);
    return true;
  }

  function init() {
    var containers = document.querySelectorAll('[data-product-description]');
    Array.prototype.forEach.call(containers, function (c) {
      try {
        build(c);
      } catch (e) {
        /* a broken description must never break the page */
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
